import {
	action,
	autorun,
	computed,
	observable,
	makeObservable,
	runInAction,
	reaction,
} from 'mobx';
import {enumToValues} from '@netx/core-next/utils/enum';
import {getUserLabel} from '@netx/core-user-next/api/getUserLabel';
import {RuleModel} from '../baseModel';
import {UserType, SearchTypeUser, TSearchTypeUser} from './constants';
import {userRuleDefaults} from './defaults';
import type {UserRule} from './types';
import {validateUserRule} from './validate';

export class UserRuleModel
	extends RuleModel<TSearchTypeUser>
	implements UserRule
{
	readonly elementType = SearchTypeUser;
	elementSubType1: UserType = userRuleDefaults.elementSubType1;
	elementSubType2: 0 = userRuleDefaults.elementSubType2;
	elementValue1 = userRuleDefaults.elementValue1;
	elementValue2: '' = userRuleDefaults.elementValue2;
	elementValue3: '' = userRuleDefaults.elementValue3;

	get elementSubType1Options() {
		return enumToValues(UserType);
	}

	get isValid() {
		const validation = validateUserRule(this);
		this.setValidationErrors(this.dirty && validation ? validation : undefined);
		if (!validation) return true;
		return false;
	}

	constructor(params?: Partial<Omit<UserRule, 'elementType'>>) {
		super();

		makeObservable(this, {
			isValid: computed,
			elementSubType1: observable,
			elementValue1: observable,
			setElementSubType1: action,
			setElementValue1: action,
		});

		runInAction(() => {
			Object.assign(this, {...userRuleDefaults, ...params});
		});

		this.reactionDisposers.push(
			autorun(() => {
				this.setElementValue1Unavailable(false);

				if (!this.elementValue1) return;

				this.setElementValue1Loading(true);

				getUserLabel({id: this.elementValue1})
					.then((userLabel) => {
						this.setElementValue1Label(userLabel);
					})
					.catch(() => {
						this.setElementValue1Unavailable(true);
					})
					.finally(() => {
						this.setElementValue1Loading(false);
					});
			}),
		);
	}

	/**
	 * Set user action to search for
	 * @param elementSubType1 UserType
	 */
	setElementSubType1(elementSubType1: UserType) {
		this.elementSubType1 = elementSubType1;
	}

	/** stubbed */
	setElementSubType2() {}

	/**
	 * Set user id
	 * @param elementValue1 user id
	 */
	setElementValue1(elementValue1: number) {
		this.elementValue1 = elementValue1;
		this.dirty = true;
	}

	/** stubbed */
	setElementValue2() {}

	/** stubbed */
	setElementValue3() {}
}

export default UserRuleModel;
